import React from "react";
import {
  Inbox,
  FolderOpen,
  Send,
  Ban,
  ShoppingCart,
  CheckCircle2,
  FileText,
  Wrench,
  RotateCcw,
  IndianRupee,
} from "lucide-react";
import { DashboardKpiItem } from "../components/Kpi/types";
import { ServiceIndividualKpiApiItem } from "../services/serviceIndividualDashboardApi";

const formatIndianCurrency = (value: number | string): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);

  if (numericValue >= 10000000) {
    const crores = numericValue / 10000000;
    return `₹${crores.toFixed(2)} Cr`;
  }
  if (numericValue >= 100000) {
    const lakhs = numericValue / 100000;
    return `₹${lakhs.toFixed(2)} L`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const formatKpiValue = (
  id: string,
  value: number | string,
): number | string => {
  if (id === "total_service_value") return formatIndianCurrency(value);
  return value;
};

export const getServiceIndividualKpis = (): DashboardKpiItem[] => [
  {
    id: "new_service_enquiries",
    label: "New Enquiries Received",
    value: 0,
    helperLabel: "Service enquiries created in selected period",
    icon: React.createElement(Inbox, { size: 16 }),
    accentColor: "#2563eb",
    accentBg: "#eff6ff",
  },
  {
    id: "open_service_enquiries",
    label: "Open Enquiry",
    value: 0,
    helperLabel: "Active Enquiries currently open",
    icon: React.createElement(FolderOpen, { size: 16 }),
    accentColor: "#dc2626",
    accentBg: "#fef2f2",
  },
  {
    id: "service_quotation_won",
    label: "Quotation Won",
    value: 0,
    helperLabel: "Service enquiries with a won quotation",
    icon: React.createElement(CheckCircle2, { size: 16 }),
    accentColor: "#16a34a",
    accentBg: "#f0fdf4",
  },
  {
    id: "service_quotation_lost",
    label: "Quotation Lost",
    value: 0,
    helperLabel: "Service enquiries with status Lost",
    icon: React.createElement(Ban, { size: 16 }),
    accentColor: "#dc2626",
    accentBg: "#fef2f2",
  },
  {
    id: "service_order_received",
    label: "Order Received",
    value: 0,
    helperLabel: "Service orders received in selected period",
    icon: React.createElement(ShoppingCart, { size: 16 }),
    accentColor: "#0d9488",
    accentBg: "#f0fdfa",
  },
  {
    id: "job_completed",
    label: "Job Completed",
    value: 0,
    helperLabel: "Service orders marked Service Completed",
    icon: React.createElement(CheckCircle2, { size: 16 }),
    accentColor: "#16a34a",
    accentBg: "#ecfdf5",
  },
  {
    id: "challan_created",
    label: "Challan Created",
    value: 0,
    helperLabel: "Service challans created in selected period",
    icon: React.createElement(FileText, { size: 16 }),
    accentColor: "#ea580c",
    accentBg: "#fff7ed",
  },
  {
    id: "accessories_used",
    label: "Accessories Used",
    value: 0,
    helperLabel: "Total quantity of accessories used in service orders",
    icon: React.createElement(Wrench, { size: 16 }),
    accentColor: "#4f46e5",
    accentBg: "#e0e7ff",
  },
  {
    id: "accessories_returned",
    label: "Accessories Returned",
    value: 0,
    helperLabel: "Total accessory return inwards created",
    icon: React.createElement(RotateCcw, { size: 16 }),
    accentColor: "#06b6d4",
    accentBg: "#ecfeff",
  },
  {
    id: "total_service_value",
    label: "Total Service Value",
    value: 0,
    helperLabel: "Grand total amount of service orders",
    icon: React.createElement(IndianRupee, { size: 16 }),
    accentColor: "#16a34a",
    accentBg: "#ecfdf5",
  },
];

export const mergeServiceIndividualKpis = (
  apiItems?: ServiceIndividualKpiApiItem[],
): DashboardKpiItem[] => {
  const apiItemsById = new Map((apiItems || []).map((item) => [item.id, item]));

  return getServiceIndividualKpis().map((item) => {
    const apiItem = apiItemsById.get(item.id);
    if (!apiItem) return item;

    return {
      ...item,
      label: apiItem.label || item.label,
      value: formatKpiValue(apiItem.id, apiItem.value),
      rawValue: apiItem.value,
      helperLabel: apiItem.helper_label || apiItem.helperLabel || item.helperLabel,
    };
  });
};
